import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ensurePwaWallet,
  fetchPwaWallet,
  fetchPwaWalletBalance,
  fetchPwaAuthUser,
  fetchPwaHarvestPositions,
  fetchPwaHoldings,
  fetchPwaSwaps,
  fetchPwaHarvestWsolBalance,
  fetchPwaBundleWallets,
  fetchPwaBundleScaleOutRule,
  sellPwaBundleWallet,
  sellAllPwaBundleWallets,
  withdrawPwaBundleWallet,
  withdrawAllPwaBundleWallets,
  updatePwaBundleScaleOutRule,
  harvestPwaPosition,
  checkSnsDomainAvailability,
  registerSnsDomain,
  unwrapPwaHarvestWsol,
  withdrawPwaSol,
  withdrawPwaHarvestSol,
  type PwaHarvestPosition,
  type PwaHolding,
  type PwaSwapEvent,
  type PwaBundleWalletDetail,
  type PwaBundleWalletWithdrawResult,
  type PwaBundleWalletWithdrawAllResult,
  type SnsAvailabilityResult,
  type TelegramPwaWallet,
  type TelegramUser,
  type PwaAuthUser
} from '../../utils/telegramAuth';
import { getRelayTokenPriceSolana } from '../../hooks/useRelayPrice';
import { useSNSName } from '../../hooks/useSNSName';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 80px);
  padding: 16px 16px 100px 16px;
  box-sizing: border-box;
  color: #ffffff;

  @media (max-width: 480px) {
    padding: 12px 12px 100px 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0 0 12px 0;
  letter-spacing: 0.4px;
`;

const SnsRegisterRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
`;

const SnsRegisterInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const SnsRegisterLabel = styled.div`
  color: rgba(203, 213, 245, 0.85);
  font-size: 12px;
  font-weight: 600;
`;

const SnsRegisterIcon = styled.img`
  width: 54px;
  height: 54px;
  object-fit: contain;
`;

const SnsRegisterButton = styled.button`
  border: none;
  background: transparent;
  color: #38bdf8;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  transition: color 0.2s ease;

  &:hover {
    color: #e0f2fe;
  }
`;

const WalletAddressButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.9);
  font-size: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  cursor: pointer;
  max-width: 210px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WalletAddressRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const WalletAddressText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
  word-break: break-all;
  text-align: center;
`;

const WalletAddressActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 2px;
`;

const WalletAddressAction = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  padding: 0;
  cursor: pointer;

  &:hover {
    color: rgba(255, 255, 255, 0.95);
  }
`;

const BalanceRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: center;
`;

const TotalUsdValue = styled.div`
  font-size: 44px;
  font-weight: 700;
  color: #10b981;
  text-align: center;
  text-shadow:
    0 1px 2px rgba(0, 0, 0, 0.45),
    0 0 12px rgba(16, 185, 129, 0.35),
    0 0 24px rgba(16, 185, 129, 0.25);
`;

const BalanceValue = styled.div`
  font-size: 12px;
  font-weight: 600;
`;

const BalanceUsd = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
`;

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
`;

const ActionButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  padding: 12px 10px;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const ActionIcon = styled.img`
  width: 22px;
  height: 22px;
  object-fit: contain;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 420px;
  background: #0b0b0f;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 18px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 18px;
  cursor: pointer;
`;

const Card = styled.div`
  background: rgba(20, 20, 25, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: rgba(255, 255, 255, 0.6);
`;

const SectionLabel = styled(Label)`
  font-size: 16.5px;
  font-weight: 700;
`;

const Value = styled.div`
  font-size: 14px;
  color: #ffffff;
  word-break: break-all;
`;

const HelperText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(20, 20, 25, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 10px 12px;
`;

const HarvestHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 8px;
`;

const HarvestToggle = styled.button`
  background: transparent;
  border: none;
  color: #38bdf8;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    color: #7dd3fc;
  }
`;

const HarvestRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HarvestBadge = styled.div<{ $locked?: boolean }>`
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  background: ${props => (props.$locked ? 'rgba(248, 113, 113, 0.2)' : 'rgba(16, 185, 129, 0.2)')};
  color: ${props => (props.$locked ? '#f87171' : '#34d399')};
`;

const HarvestButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  padding: 6px 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const HarvestLink = styled.a`
  color: #38bdf8;
  font-size: 12px;
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    color: #7dd3fc;
  }
`;

const SwapLabel = styled.div<{ $direction: 'buy' | 'sell' }>`
  color: ${props => props.$direction === 'buy' ? '#10b981' : '#ffffff'};
  font-weight: 600;
`;

const HoldingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HoldingLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 10px;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.06);
`;

const HoldingSymbol = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const TradeButton = styled.button`
  margin-left: 8px;
  padding: 6px 10px;
  height: 28px;
  min-width: 64px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: linear-gradient(135deg, #14F195 0%, #10b981 100%);
  color: #000000;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(20, 241, 149, 0.3);
  }
`;

const HoldingRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const HoldingAmount = styled.div`
  font-size: 13px;
  font-weight: 600;
`;

const HoldingUsd = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const HoldingRoi = styled.div<{ $positive?: boolean | null }>`
  font-size: 12px;
  color: ${props => {
    if (props.$positive === true) return '#10b981';
    if (props.$positive === false) return '#ef4444';
    return 'rgba(255, 255, 255, 0.6)';
  }};
`;


const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  font-size: 13px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  color: #ffffff;
  font-size: 13px;
`;

const ModalActionButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const AvailabilityRow = styled.div<{ $available: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.$available ? '#10b981' : '#ef4444'};
`;

const AvailabilityIcon = styled.span`
  font-size: 16px;
  line-height: 1;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
`;

const ToggleRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const BundleWalletList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
`;

const BundleWalletCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BundleWalletHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const BundleWalletIndex = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
`;

const BundleWalletAddressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 12px;
`;

const BundleWalletMetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
`;

const BundleWalletValue = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #ffffff;
`;

const BundleWalletActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;

const BundleWalletActionButton = styled.button<{ $danger?: boolean }>`
  padding: 5px 8px;
  border-radius: 6px;
  border: 1px solid ${props => props.$danger ? 'rgba(239, 68, 68, 0.6)' : 'rgba(255, 255, 255, 0.15)'};
  background: ${props => props.$danger ? 'rgba(239, 68, 68, 0.2)' : 'transparent'};
  color: ${props => props.$danger ? '#fecaca' : '#ffffff'};
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
`;

const BundleButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BundleTotalsRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
`;

const BundleTotalsValue = styled.span`
  font-weight: 600;
  color: #ffffff;
`;

const BundleActionButton = styled.button<{ $secondary?: boolean; $danger?: boolean; $active?: boolean }>`
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid ${props => {
    if (props.$danger) return 'rgba(248, 113, 113, 0.6)';
    if (props.$active) return 'rgba(56, 189, 248, 0.7)';
    return 'rgba(56, 189, 248, 0.4)';
  }};
  background: ${props => {
    if (props.$danger) return 'rgba(248, 113, 113, 0.25)';
    if (props.$active) return 'rgba(56, 189, 248, 0.25)';
    return props.$secondary ? 'rgba(255, 255, 255, 0.04)' : 'rgba(56, 189, 248, 0.12)';
  }};
  color: ${props => {
    if (props.$danger) return '#fecaca';
    if (props.$active) return '#7dd3fc';
    return props.$secondary ? 'rgba(255, 255, 255, 0.8)' : '#7dd3fc';
  }};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const BundleWarning = styled.div`
  font-size: 12px;
  color: #fca5a5;
`;

const BundleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const BundleToggle = styled.button`
  border: none;
  background: transparent;
  color: rgba(148, 163, 184, 0.9);
  font-size: 12px;
  cursor: pointer;
`;

const ScaleOutOptionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ScaleOutOptionButton = styled.button<{ $active?: boolean }>`
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  color: ${props => (props.$active ? '#7dd3fc' : 'rgba(148, 163, 184, 0.9)')};
  background: ${props => (props.$active ? 'rgba(56, 189, 248, 0.15)' : 'transparent')};
  cursor: pointer;
`;
interface SentryWalletPWAProps {
  authLabel?: string;
  basePath?: string;
}

const SentryWalletPWA: React.FC<SentryWalletPWAProps> = ({
  authLabel = 'Telegram',
  basePath = '/sentry-pwa'
}) => {
  const [telegramUser, setTelegramUser] = useState<PwaAuthUser | TelegramUser | null>(null);
  const [wallet, setWallet] = useState<TelegramPwaWallet | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [balanceSol, setBalanceSol] = useState<number | null>(null);
  const [holdings, setHoldings] = useState<PwaHolding[] | null>(null);
  const [recentSwaps, setRecentSwaps] = useState<PwaSwapEvent[]>([]);
  const [bundleWalletCount, setBundleWalletCount] = useState('50');
  const [bundleWallets, setBundleWallets] = useState<string[]>([]);
  const [bundleWalletDetails, setBundleWalletDetails] = useState<PwaBundleWalletDetail[]>([]);
  const [bundleWalletsLoading, setBundleWalletsLoading] = useState(false);
  const [bundleWalletsError, setBundleWalletsError] = useState<string | null>(null);
  const [bundleWalletsStatus, setBundleWalletsStatus] = useState<string | null>(null);
  const [bundleResetOpen, setBundleResetOpen] = useState(false);
  const [bundleWalletsSelling, setBundleWalletsSelling] = useState<Record<string, boolean>>({});
  const [bundleWalletsSellingAll, setBundleWalletsSellingAll] = useState(false);
  const [bundleSellAllOpen, setBundleSellAllOpen] = useState(false);
  const [bundleWalletsWithdrawing, setBundleWalletsWithdrawing] = useState<Record<string, boolean>>({});
  const [bundleWalletsWithdrawingAll, setBundleWalletsWithdrawingAll] = useState(false);
  const [bundleWithdrawAllOpen, setBundleWithdrawAllOpen] = useState(false);
  const [bundleWithdrawToMain, setBundleWithdrawToMain] = useState(true);
  const [bundleWithdrawAddress, setBundleWithdrawAddress] = useState('');
  const [bundleExpanded, setBundleExpanded] = useState(false);
  const [bundleTokenFilterMint, setBundleTokenFilterMint] = useState('all');
  const [bundleWalletSellResults, setBundleWalletSellResults] = useState<Record<string, {
    signature: string;
    tokensSold: number;
    solReceived: number;
    mint: string;
    symbol?: string | null;
  }>>({});
  const [bundleWalletWithdrawResults, setBundleWalletWithdrawResults] = useState<Record<string, {
    signature: string;
    amountSol: number;
  }>>({});
  const [scaleOutMint, setScaleOutMint] = useState('');
  const [scaleOutTargetMarketCap, setScaleOutTargetMarketCap] = useState('');
  const [scaleOutPercent, setScaleOutPercent] = useState<'10' | '20' | '30'>('30');
  const [scaleOutEnabled, setScaleOutEnabled] = useState(true);
  const [scaleOutLoading, setScaleOutLoading] = useState(false);
  const [scaleOutStatus, setScaleOutStatus] = useState<string | null>(null);
  const [scaleOutError, setScaleOutError] = useState<string | null>(null);
  const [scaleOutExpanded, setScaleOutExpanded] = useState(false);
  const [scaleOutTokenPercent, setScaleOutTokenPercent] = useState<'10' | '25' | '50' | '75' | 'max'>('max');
  const navigate = useNavigate();
  const [harvestPositions, setHarvestPositions] = useState<PwaHarvestPosition[]>([]);
  const [harvestLoading, setHarvestLoading] = useState(false);
  const [harvestError, setHarvestError] = useState<string | null>(null);
  const [harvestExpanded, setHarvestExpanded] = useState(false);
  const [harvestStatus, setHarvestStatus] = useState<string | null>(null);
  const [harvestLink, setHarvestLink] = useState<string | null>(null);
  const [harvestingPosition, setHarvestingPosition] = useState<string | null>(null);
  const [wsolBalance, setWsolBalance] = useState<number | null>(null);
  const [unwrapStatus, setUnwrapStatus] = useState<string | null>(null);
  const [unwrapping, setUnwrapping] = useState(false);
  const [harvestWithdrawAddress, setHarvestWithdrawAddress] = useState('');
  const [harvestWithdrawAmount, setHarvestWithdrawAmount] = useState('');
  const [harvestWithdrawAll, setHarvestWithdrawAll] = useState(false);
  const [harvestWithdrawToMain, setHarvestWithdrawToMain] = useState(true);
  const [harvestWithdrawStatus, setHarvestWithdrawStatus] = useState<string | null>(null);
  const [harvestWithdrawLoading, setHarvestWithdrawLoading] = useState(false);
  const [solPriceUsd, setSolPriceUsd] = useState<number | null>(null);
  const [tokenPricesUsd, setTokenPricesUsd] = useState<Record<string, number>>({});
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawAll, setWithdrawAll] = useState(false);
  const [withdrawStatus, setWithdrawStatus] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingSwaps, setLoadingSwaps] = useState(false);
  const [recentSwapsExpanded, setRecentSwapsExpanded] = useState(false);
  const [copiedStatus, setCopiedStatus] = useState<string | null>(null);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [snsModalOpen, setSnsModalOpen] = useState(false);
  const [walletHidden, setWalletHidden] = useState(false);
  const [snsDomainInput, setSnsDomainInput] = useState('');
  const [snsCheckResult, setSnsCheckResult] = useState<SnsAvailabilityResult | null>(null);
  const [snsCheckStatus, setSnsCheckStatus] = useState<string | null>(null);
  const [snsRegisterStatus, setSnsRegisterStatus] = useState<string | null>(null);
  const [snsChecking, setSnsChecking] = useState(false);
  const [snsRegistering, setSnsRegistering] = useState(false);
  const [snsRefreshKey, setSnsRefreshKey] = useState(0);
  const effectiveSolPrice = solPriceUsd ?? 0;
  const { snsName } = useSNSName(wallet?.walletAddress, Boolean(wallet), snsRefreshKey);

  const holdingsByMint = useMemo(() => {
    const map = new Map<string, { symbol?: string | null; decimals: number }>();
    (holdings || []).forEach((holding) => {
      map.set(holding.mintAddress, { symbol: holding.symbol, decimals: holding.decimals });
    });
    return map;
  }, [holdings]);

  const formatWalletAddress = (address: string) => {
    if (!address) return '';
    if (address.length <= 10) return address;
    return `${address.slice(0, 5)}...${address.slice(-5)}`;
  };

  const formatNumberWithCommas = (value: string) => {
    if (!value) return '';
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';
    return numeric.toLocaleString('en-US');
  };

  const refreshPrices = async (nextHoldings?: PwaHolding[] | null, extraMints?: string[]) => {
    const holdingsList = nextHoldings || holdings || [];
    const solPrice = await getRelayTokenPriceSolana('So11111111111111111111111111111111111111112');
    setSolPriceUsd(solPrice);

    const prices: Record<string, number> = {};
    const mints = new Set<string>();
    holdingsList.forEach((holding) => mints.add(holding.mintAddress));
    (extraMints || []).forEach((mint) => mints.add(mint));

    await Promise.all(
      Array.from(mints).map(async (mint) => {
        const price = await getRelayTokenPriceSolana(mint);
        if (price) {
          prices[mint] = price;
        }
      })
    );
    setTokenPricesUsd(prices);
  };

  const bundleTokenMints = useMemo(() => {
    const mints = new Set<string>();
    bundleWalletDetails.forEach((detail) => {
      detail.tokenHoldings.forEach((holding) => mints.add(holding.mint));
    });
    return Array.from(mints);
  }, [bundleWalletDetails]);

  const bundleTokenOptions = useMemo(() => {
    const map = new Map<string, { mint: string; symbol?: string | null }>();
    bundleWalletDetails.forEach((detail) => {
      detail.tokenHoldings.forEach((holding) => {
        if (!map.has(holding.mint)) {
          map.set(holding.mint, { mint: holding.mint, symbol: holding.symbol });
        }
      });
    });
    return Array.from(map.values());
  }, [bundleWalletDetails]);

  const scaleOutTokenTotals = useMemo(() => {
    if (!scaleOutMint) {
      return { totalTokens: 0, symbol: null };
    }
    let totalTokens = 0;
    let symbol: string | null = null;
    bundleWalletDetails.forEach((detail) => {
      detail.tokenHoldings.forEach((holding) => {
        if (holding.mint === scaleOutMint) {
          totalTokens += holding.amount;
          symbol = symbol || holding.symbol || null;
        }
      });
    });
    return { totalTokens, symbol };
  }, [bundleWalletDetails, scaleOutMint]);

  const scaleOutTokenPercentValue = scaleOutTokenPercent === 'max' ? 100 : Number(scaleOutTokenPercent);
  const scaleOutTokenTargetAmount = scaleOutTokenTotals.totalTokens * (scaleOutTokenPercentValue / 100);
  const scaleOutTargetMarketCapValue = scaleOutTargetMarketCap ? Number(scaleOutTargetMarketCap) : 0;
  const scaleOutEstimatedUsd = scaleOutTargetMarketCapValue > 0
    ? (scaleOutTokenTargetAmount / 1_000_000_000) * scaleOutTargetMarketCapValue
    : 0;

  useEffect(() => {
    if (bundleTokenMints.length === 0) return;
    refreshPrices(undefined, bundleTokenMints);
  }, [bundleTokenMints]);

  useEffect(() => {
    if (!scaleOutMint && bundleTokenOptions.length > 0) {
      setScaleOutMint(bundleTokenOptions[0].mint);
    }
  }, [scaleOutMint, bundleTokenOptions]);

  useEffect(() => {
    if (bundleTokenFilterMint !== 'all' && bundleTokenFilterMint !== scaleOutMint) {
      setScaleOutMint(bundleTokenFilterMint);
    }
  }, [bundleTokenFilterMint, scaleOutMint]);


  useEffect(() => {
    if (!scaleOutMint) return;
    let cancelled = false;

    const loadRule = async () => {
      setScaleOutLoading(true);
      setScaleOutError(null);
      try {
        const rule = await fetchPwaBundleScaleOutRule(scaleOutMint);
        if (cancelled) return;
        if (rule) {
          setScaleOutTargetMarketCap(rule.targetMarketCapUsd.toString());
          setScaleOutPercent(String(rule.scaleOutPercent) as '10' | '20' | '30');
          setScaleOutEnabled(Boolean(rule.enabled));
        } else {
          setScaleOutTargetMarketCap('');
          setScaleOutPercent('30');
          setScaleOutEnabled(true);
        }
      } catch (error: any) {
        if (!cancelled) {
          setScaleOutError(error?.message || 'Unable to load scale out settings.');
        }
      } finally {
        if (!cancelled) {
          setScaleOutLoading(false);
        }
      }
    };

    loadRule();

    return () => {
      cancelled = true;
    };
  }, [scaleOutMint]);

  const bundleWalletDisplay = useMemo(() => {
    const sorted = [...bundleWalletDetails].sort((a, b) => a.walletIndex - b.walletIndex);
    return sorted.map((detail) => {
      const solBalance = detail.solBalanceLamports / 1_000_000_000;
      const tokenHoldings = detail.tokenHoldings.map((holding) => {
        const price = tokenPricesUsd[holding.mint] || 0;
        return {
          ...holding,
          usdValue: holding.amount * price
        };
      });
      const totalTokenUsd = tokenHoldings.reduce((sum, holding) => sum + holding.usdValue, 0);
      const primaryHolding = tokenHoldings.reduce((best, holding) => {
        if (!best || holding.usdValue > best.usdValue) return holding;
        return best;
      }, undefined as (typeof tokenHoldings)[number] | undefined);
      const filteredHolding = bundleTokenFilterMint !== 'all'
        ? tokenHoldings.find((holding) => holding.mint === bundleTokenFilterMint)
        : primaryHolding;
      const filteredTokenUsd = bundleTokenFilterMint !== 'all'
        ? (filteredHolding?.usdValue ?? 0)
        : totalTokenUsd;

      return {
        walletAddress: detail.walletAddress,
        walletIndex: detail.walletIndex,
        solBalance,
        totalTokenUsd,
        primaryHolding,
        filteredHolding,
        filteredTokenUsd
      };
    });
  }, [bundleWalletDetails, tokenPricesUsd, bundleTokenFilterMint]);

  const bundleTokenTotals = useMemo(() => {
    if (bundleTokenFilterMint === 'all') {
      return { totalTokens: 0, totalUsd: 0, symbol: '' };
    }
    let totalTokens = 0;
    let totalUsd = 0;
    let symbol = '';
    bundleWalletDetails.forEach((detail) => {
      detail.tokenHoldings.forEach((holding) => {
        if (holding.mint === bundleTokenFilterMint) {
          totalTokens += holding.amount;
          const price = tokenPricesUsd[holding.mint] || 0;
          totalUsd += holding.amount * price;
          if (!symbol) {
            symbol = holding.symbol || '';
          }
        }
      });
    });
    return { totalTokens, totalUsd, symbol };
  }, [bundleWalletDetails, bundleTokenFilterMint, tokenPricesUsd]);

  const bundleTotals = useMemo(() => {
    const solTotal = bundleWalletDisplay.reduce((sum, wallet) => sum + wallet.solBalance, 0);
    const tokenUsdTotal = bundleTokenFilterMint === 'all'
      ? bundleWalletDisplay.reduce((sum, wallet) => sum + wallet.totalTokenUsd, 0)
      : bundleTokenTotals.totalUsd;
    const totalUsd = (solTotal * effectiveSolPrice) + tokenUsdTotal;
    return { solTotal, tokenUsdTotal, totalUsd };
  }, [bundleWalletDisplay, effectiveSolPrice, bundleTokenFilterMint, bundleTokenTotals]);

  const totalUsdSummary = useMemo(() => {
    const mainSolUsd = (balanceSol ?? 0) * effectiveSolPrice;
    const harvestWsolUsd = (wsolBalance ?? 0) * effectiveSolPrice;
    const mainTokenUsd = (holdings || []).reduce((sum, holding) => {
      const price = tokenPricesUsd[holding.mintAddress] || 0;
      return sum + (holding.amount * price);
    }, 0);
    const bundleTokenUsd = bundleWalletDetails.reduce((sum, detail) => {
      return sum + detail.tokenHoldings.reduce((inner, holding) => {
        const price = tokenPricesUsd[holding.mint] || 0;
        return inner + (holding.amount * price);
      }, 0);
    }, 0);
    const totalUsd = mainSolUsd + harvestWsolUsd + mainTokenUsd + bundleTokenUsd;
    return { totalUsd, mainSolUsd, harvestWsolUsd, mainTokenUsd, bundleTokenUsd };
  }, [balanceSol, effectiveSolPrice, wsolBalance, holdings, tokenPricesUsd, bundleWalletDetails]);

  const refreshSwapFeed = async () => {
    setLoadingSwaps(true);
    try {
      const swaps = await fetchPwaSwaps(10);
      setRecentSwaps(swaps || []);
    } finally {
      setLoadingSwaps(false);
    }
  };

  const refreshHarvestPositions = async () => {
    setHarvestLoading(true);
    setHarvestError(null);
    try {
      const positions = await fetchPwaHarvestPositions();
      setHarvestPositions(positions || []);
    } catch (error: any) {
      setHarvestError(error?.message || 'Unable to load LP positions.');
    } finally {
      setHarvestLoading(false);
    }
  };

  const refreshWsolBalance = async () => {
    const balance = await fetchPwaHarvestWsolBalance();
    setWsolBalance(balance?.amountSol ?? 0);
  };

  useEffect(() => {
    let cancelled = false;

    const loadWallet = async () => {
      setLoading(true);
      setError(null);
      try {
        const user = await fetchPwaAuthUser();
        if (cancelled) return;
        setTelegramUser(user);

        if (!user) {
          setWallet(null);
          return;
        }

        const existing = await fetchPwaWallet();
        if (cancelled) return;

        if (existing) {
          setWallet(existing);
        } else {
          const created = await ensurePwaWallet();
          if (!cancelled) {
            setWallet(created);
          }
        }
        const balance = await fetchPwaWalletBalance();
        if (!cancelled) {
          setBalanceSol(balance?.balanceSol ?? null);
        }
        const nextHoldings = await fetchPwaHoldings();
        if (!cancelled) {
          setHoldings(nextHoldings);
        }
        await refreshPrices(nextHoldings);
        await refreshSwapFeed();
        await refreshHarvestPositions();
        await refreshWsolBalance();
        await loadBundleWallets({ count: 50 });
      } catch (walletError: any) {
        if (!cancelled) {
          setError(walletError?.message || 'Unable to load wallet.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadWallet();

    return () => {
      cancelled = true;
    };
  }, []);

  const canWithdraw = useMemo(() => {
    if (!telegramUser || !wallet) return false;
    if (!withdrawAddress.trim()) return false;
    if (withdrawAll) return true;
    const amount = Number(withdrawAmount);
    return Number.isFinite(amount) && amount > 0;
  }, [telegramUser, wallet, withdrawAddress, withdrawAmount, withdrawAll]);

  const canHarvestWithdraw = useMemo(() => {
    if (!telegramUser || !wallet) return false;
    if (!harvestWithdrawToMain && !harvestWithdrawAddress.trim()) return false;
    if (harvestWithdrawAll) return true;
    const amount = Number(harvestWithdrawAmount);
    return Number.isFinite(amount) && amount > 0;
  }, [
    telegramUser,
    wallet,
    harvestWithdrawToMain,
    harvestWithdrawAddress,
    harvestWithdrawAll,
    harvestWithdrawAmount
  ]);

  const refreshBalances = async () => {
    setRefreshing(true);
    try {
      const balance = await fetchPwaWalletBalance();
      setBalanceSol(balance?.balanceSol ?? null);
      const nextHoldings = await fetchPwaHoldings();
      setHoldings(nextHoldings);
      await refreshPrices(nextHoldings || undefined);
      await refreshSwapFeed();
      await refreshHarvestPositions();
      await refreshWsolBalance();
      await loadBundleWallets();
    } finally {
      setRefreshing(false);
    }
  };

  const formatSwapTokenAmount = (swap: PwaSwapEvent) => {
    if (swap.tokenAmount !== null && swap.tokenAmount !== undefined) {
      return swap.tokenAmount.toLocaleString(undefined, { maximumFractionDigits: 6 });
    }
    if (swap.amountIn && swap.direction === 'sell') {
      const base = Number(swap.amountIn);
      if (Number.isFinite(base)) {
        const decimals = holdingsByMint.get(swap.mintAddress)?.decimals ?? 9;
        const amount = base / Math.pow(10, decimals);
        return amount.toLocaleString(undefined, { maximumFractionDigits: 6 });
      }
    }
    return null;
  };

  const copyBundleWallets = async () => {
    if (bundleWallets.length === 0) return;
    try {
      await navigator.clipboard.writeText(bundleWallets.join('\n'));
      setBundleWalletsStatus('Bundle wallet list copied.');
    } catch {
      setBundleWalletsStatus('Unable to copy bundle wallet list.');
    }
    setTimeout(() => setBundleWalletsStatus(null), 2000);
  };

  const loadBundleWallets = async (options?: { reset?: boolean; count?: number }) => {
    const count = Number(options?.count ?? bundleWalletCount);
    if (!Number.isFinite(count) || count < 1 || count > 50) {
      setBundleWalletsError('Bundle wallet count must be between 1 and 50.');
      return;
    }
    setBundleWalletsLoading(true);
    setBundleWalletsError(null);
    setBundleWalletsStatus(null);
    try {
      const result = await fetchPwaBundleWallets(Math.floor(count), { reset: options?.reset, details: true });
      setBundleWallets(result.wallets || []);
      setBundleWalletDetails(result.details || []);
      setBundleWalletsStatus(options?.reset ? 'Bundle wallets reset.' : 'Bundle wallets loaded.');
    } catch (error: any) {
      setBundleWalletsError(error?.message || 'Unable to load bundle wallets.');
    } finally {
      setBundleWalletsLoading(false);
    }
  };

  const truncateAddress = (value: string) => {
    if (!value) return '';
    return `${value.slice(0, 4)}...${value.slice(-4)}`;
  };

  const copyBundleWallet = async (address: string) => {
    try {
      await navigator.clipboard.writeText(address);
      setBundleWalletsStatus('Bundle wallet copied.');
    } catch {
      setBundleWalletsStatus('Unable to copy bundle wallet.');
    }
    setTimeout(() => setBundleWalletsStatus(null), 2000);
  };

  const handleSellBundleWallet = async (walletAddress: string, mintAddress: string) => {
    setBundleWalletsSelling((prev) => ({ ...prev, [walletAddress]: true }));
    setBundleWalletsStatus(null);
    try {
      const result = await sellPwaBundleWallet({ walletAddress, mintAddress });
      setBundleWalletSellResults((prev) => ({
        ...prev,
        [walletAddress]: {
          signature: result.signature,
          tokensSold: result.tokensSold,
          solReceived: result.solReceived,
          mint: result.mint,
          symbol: result.symbol ?? null
        }
      }));
      setBundleWalletsStatus('Bundle wallet sold.');
      await loadBundleWallets();
    } catch (error: any) {
      setBundleWalletsStatus(error?.message || 'Bundle wallet sell failed.');
    } finally {
      setBundleWalletsSelling((prev) => ({ ...prev, [walletAddress]: false }));
    }
  };

  const handleSellAllBundleWallets = async () => {
    setBundleWalletsSellingAll(true);
    setBundleWalletsStatus(null);
    try {
      const result = await sellAllPwaBundleWallets();
      const successCount = result.results.filter((item) => item.signature).length;
      const failCount = result.results.filter((item) => item.error).length;
      const nextResults: Record<string, { signature: string; tokensSold: number; solReceived: number; mint: string; symbol?: string | null }> = {};
      result.results.forEach((item) => {
        if (item.signature && item.tokensSold !== undefined && item.solReceived !== undefined) {
          nextResults[item.walletAddress] = {
            signature: item.signature,
            tokensSold: item.tokensSold,
            solReceived: item.solReceived,
            mint: item.mint,
            symbol: item.symbol ?? null
          };
        }
      });
      setBundleWalletSellResults((prev) => ({ ...prev, ...nextResults }));
      setBundleWalletsStatus(`Sell all complete. Success: ${successCount}, Failed: ${failCount}.`);
      await loadBundleWallets();
    } catch (error: any) {
      setBundleWalletsStatus(error?.message || 'Sell all failed.');
    } finally {
      setBundleWalletsSellingAll(false);
    }
  };

  const handleWithdrawBundleWallet = async (walletAddress: string) => {
    setBundleWalletsWithdrawing((prev) => ({ ...prev, [walletAddress]: true }));
    setBundleWalletsStatus(null);
    try {
      const result = await withdrawPwaBundleWallet({
        walletAddress,
        toAddress: bundleWithdrawToMain ? undefined : bundleWithdrawAddress.trim()
      });
      setBundleWalletWithdrawResults((prev) => ({
        ...prev,
        [walletAddress]: {
          signature: result.signature,
          amountSol: result.amountSol
        }
      }));
      setBundleWalletsStatus('Bundle wallet SOL withdrawn.');
      await loadBundleWallets();
    } catch (error: any) {
      setBundleWalletsStatus(error?.message || 'Bundle wallet withdraw failed.');
    } finally {
      setBundleWalletsWithdrawing((prev) => ({ ...prev, [walletAddress]: false }));
    }
  };

  const handleWithdrawAllBundleWallets = async () => {
    setBundleWalletsWithdrawingAll(true);
    setBundleWalletsStatus(null);
    try {
      const result = await withdrawAllPwaBundleWallets({
        toAddress: bundleWithdrawToMain ? undefined : bundleWithdrawAddress.trim()
      });
      const successCount = result.results.filter((item) => item.signature).length;
      const failCount = result.results.filter((item) => item.error).length;
      const nextResults: Record<string, { signature: string; amountSol: number }> = {};
      result.results.forEach((item) => {
        if (item.signature && item.amountSol !== undefined) {
          nextResults[item.walletAddress] = {
            signature: item.signature,
            amountSol: item.amountSol
          };
        }
      });
      setBundleWalletWithdrawResults((prev) => ({ ...prev, ...nextResults }));
      setBundleWalletsStatus(`Withdraw all complete. Success: ${successCount}, Failed: ${failCount}.`);
      await loadBundleWallets();
    } catch (error: any) {
      setBundleWalletsStatus(error?.message || 'Withdraw all failed.');
    } finally {
      setBundleWalletsWithdrawingAll(false);
    }
  };

  const handleSaveScaleOut = async () => {
    setScaleOutStatus(null);
    setScaleOutError(null);

    if (!scaleOutMint) {
      setScaleOutError('Select a token to configure scale out.');
      return;
    }
    const targetCap = Number(scaleOutTargetMarketCap);
    if (!Number.isFinite(targetCap) || targetCap <= 0) {
      setScaleOutError('Target market cap must be a positive number.');
      return;
    }
    if (!['10', '20', '30'].includes(scaleOutPercent)) {
      setScaleOutError('Scale out percentage must be 10, 20, or 30.');
      return;
    }

    setScaleOutLoading(true);
    try {
      const rule = await updatePwaBundleScaleOutRule({
        mintAddress: scaleOutMint,
        targetMarketCapUsd: targetCap,
        scaleOutPercent: Number(scaleOutPercent) as 10 | 20 | 30,
        enabled: scaleOutEnabled
      });
      setScaleOutTargetMarketCap(rule.targetMarketCapUsd.toString());
      setScaleOutPercent(String(rule.scaleOutPercent) as '10' | '20' | '30');
      setScaleOutEnabled(Boolean(rule.enabled));
      setScaleOutStatus('Scale out settings saved.');
    } catch (error: any) {
      setScaleOutError(error?.message || 'Failed to save scale out settings.');
    } finally {
      setScaleOutLoading(false);
    }
  };

  const handleCopyWallet = async () => {
    if (!wallet?.walletAddress) return;
    try {
      await navigator.clipboard.writeText(wallet.walletAddress);
      setCopiedStatus('Address copied.');
    } catch {
      setCopiedStatus('Copy failed.');
    }
    setTimeout(() => setCopiedStatus(null), 2000);
  };

  const handleCopyHarvestWallet = async () => {
    if (!wallet?.harvestWalletAddress) return;
    try {
      await navigator.clipboard.writeText(wallet.harvestWalletAddress);
      setCopiedStatus('Harvest address copied.');
    } catch {
      setCopiedStatus('Copy failed.');
    }
    setTimeout(() => setCopiedStatus(null), 2000);
  };

  const handleWithdraw = async () => {
    if (!canWithdraw) return;
    setWithdrawStatus(null);
    try {
      const response = await withdrawPwaSol({
        toAddress: withdrawAddress.trim(),
        amountSol: withdrawAll ? undefined : Number(withdrawAmount),
        withdrawAll
      });
      setWithdrawStatus(`Withdrawal sent: ${response.signature}`);
      await refreshBalances();
      if (!withdrawAll) {
        setWithdrawAmount('');
      }
      setWithdrawOpen(false);
    } catch (withdrawError: any) {
      setWithdrawStatus(withdrawError?.message || 'Withdraw failed.');
    }
  };

  const openSnsModal = () => {
    setSnsModalOpen(true);
    setSnsCheckResult(null);
    setSnsCheckStatus(null);
    setSnsRegisterStatus(null);
  };

  const handleCheckSnsDomain = async () => {
    const candidate = snsDomainInput.trim();
    if (!candidate) return;
    setSnsChecking(true);
    setSnsCheckResult(null);
    setSnsCheckStatus(null);
    setSnsRegisterStatus(null);
    try {
      const result = await checkSnsDomainAvailability(candidate);
      setSnsCheckResult(result);
    } catch (error: any) {
      setSnsCheckStatus(error?.message || 'Unable to check domain.');
    } finally {
      setSnsChecking(false);
    }
  };

  const handleRegisterSnsDomain = async () => {
    if (!snsCheckResult?.available) return;
    setSnsRegistering(true);
    setSnsRegisterStatus(null);
    try {
      const result = await registerSnsDomain(snsDomainInput.trim());
      setSnsRegisterStatus(`Registered ${result.domain} successfully.`);
      setSnsRefreshKey((prev) => prev + 1);
    } catch (error: any) {
      setSnsRegisterStatus(error?.message || 'Registration failed.');
    } finally {
      setSnsRegistering(false);
    }
  };

  const handleHarvestPosition = async (position: PwaHarvestPosition) => {
    if (!position.positionMint) return;
    setHarvestStatus(null);
    setHarvestLink(null);
    setHarvestingPosition(position.positionMint);
    try {
      const result = await harvestPwaPosition(position.positionMint);
      const label = result.label || position.label;
      const wsolMint = 'So11111111111111111111111111111111111111112';
      const wsolAmount = result.mintA === wsolMint
        ? result.harvestedA
        : result.mintB === wsolMint
          ? result.harvestedB
          : 0;
      const amountText = wsolAmount > 0 ? `${wsolAmount.toFixed(6)} WSOL` : '0 WSOL';
      setHarvestStatus(`Harvested ${amountText} into your harvest wallet (${label}).`);
      setHarvestLink(`https://solscan.io/tx/${result.sig}`);
      await refreshBalances();
    } catch (error: any) {
      setHarvestStatus(error?.message || 'Harvest failed.');
    } finally {
      setHarvestingPosition(null);
    }
  };

  const handleUnwrapWsol = async () => {
    setUnwrapStatus(null);
    setUnwrapping(true);
    try {
      const result = await unwrapPwaHarvestWsol();
      setUnwrapStatus(`Unwrapped ${result.amountSol.toFixed(6)} SOL.`);
      await refreshBalances();
    } catch (error: any) {
      setUnwrapStatus(error?.message || 'Unwrap failed.');
    } finally {
      setUnwrapping(false);
    }
  };

  const handleHarvestWithdraw = async () => {
    if (!canHarvestWithdraw) return;
    setHarvestWithdrawStatus(null);
    setHarvestWithdrawLoading(true);
    try {
      const response = await withdrawPwaHarvestSol({
        toAddress: harvestWithdrawToMain ? undefined : harvestWithdrawAddress.trim(),
        toMainWallet: harvestWithdrawToMain,
        amountSol: harvestWithdrawAll ? undefined : Number(harvestWithdrawAmount),
        withdrawAll: harvestWithdrawAll
      });
      setHarvestWithdrawStatus(`Withdrawal sent: ${response.signature}`);
      await refreshBalances();
      if (!harvestWithdrawAll) {
        setHarvestWithdrawAmount('');
      }
      if (!harvestWithdrawToMain) {
        setHarvestWithdrawAddress('');
      }
    } catch (withdrawError: any) {
      setHarvestWithdrawStatus(withdrawError?.message || 'Withdraw failed.');
    } finally {
      setHarvestWithdrawLoading(false);
    }
  };

  return (
    <PageContainer>
      <PageTitle>Wallet</PageTitle>
      <Card>
        {loading && <HelperText>Loading wallet…</HelperText>}
        {!loading && error && <HelperText>{error}</HelperText>}
        {!loading && !error && !telegramUser && (
          <HelperText>Log in with {authLabel} to view your Sentry wallet.</HelperText>
        )}
        {!loading && !error && telegramUser && wallet && (
          <>
            {copiedStatus && <HelperText>{copiedStatus}</HelperText>}
            <TotalUsdValue>
              {`$${totalUsdSummary.totalUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            </TotalUsdValue>
            <WalletAddressRow>
              <WalletAddressText>
                {walletHidden ? '***********' : (snsName || wallet.walletAddress)}
              </WalletAddressText>
            </WalletAddressRow>
            <BalanceRow>
              <BalanceValue>
                {walletHidden
                  ? '***** SOL'
                  : balanceSol !== null
                    ? `${balanceSol.toFixed(6)} SOL`
                    : '—'}
              </BalanceValue>
              <BalanceUsd>
                {walletHidden
                  ? '*****'
                  : balanceSol !== null
                    ? `$${(balanceSol * effectiveSolPrice).toFixed(2)}`
                    : '$0.00'}
              </BalanceUsd>
            </BalanceRow>
            <WalletAddressActions>
              <WalletAddressAction type="button" onClick={() => setWalletHidden((prev) => !prev)}>
                {walletHidden ? 'Show' : 'Hide'}
              </WalletAddressAction>
              <WalletAddressAction type="button" onClick={handleCopyWallet}>
                Copy
              </WalletAddressAction>
            </WalletAddressActions>
            <ActionRow>
              <ActionButton type="button" onClick={handleCopyWallet}>
                <ActionIcon src="/sentry-wallet-fund.png" alt="Fund" />
                Fund
              </ActionButton>
              <ActionButton type="button" onClick={() => setWithdrawOpen(true)}>
                <ActionIcon src="/sentry-wallet-withdraw.png" alt="Withdraw" />
                Withdraw
              </ActionButton>
              <ActionButton type="button" onClick={refreshBalances} disabled={refreshing}>
                <ActionIcon src="/sentry-wallet-refresh.png" alt="Refresh" />
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </ActionButton>
            </ActionRow>
            <SectionLabel>Token Holdings</SectionLabel>
            {holdings && holdings.filter((holding) => holding.amount > 0.0000015).length > 0 ? (
              <List>
                {holdings
                  .filter((holding) => holding.amount > 0.0000015)
                  .map((holding) => {
                  const symbol = holding.symbol || holding.name || 'TOKEN';
                  const currentPrice = tokenPricesUsd[holding.mintAddress] || 0;
                  const usdValue = holding.amount * currentPrice;
                  const roiPct = holding.entryPriceUsd && currentPrice
                    ? ((currentPrice - holding.entryPriceUsd) / holding.entryPriceUsd) * 100
                    : null;
                  return (
                    <ListItem key={holding.mintAddress}>
                      <HoldingLeft>
                        <HoldingLogo
                          src={holding.logoUrl || '/token-placeholder.png'}
                          alt={`${symbol} logo`}
                          onError={(event) => {
                            (event.target as HTMLImageElement).src = '/token-placeholder.png';
                          }}
                        />
                        <HoldingSymbol>{symbol}</HoldingSymbol>
                        <TradeButton type="button" onClick={() => navigate(`${basePath}/swap?mint=${holding.mintAddress}`)}>
                          Trade
                        </TradeButton>
                      </HoldingLeft>
                      <HoldingRight>
                        <HoldingAmount>
                          {holding.amount.toLocaleString('en-US', { maximumFractionDigits: 6 })}
                        </HoldingAmount>
                        <HoldingUsd>${usdValue.toFixed(2)}</HoldingUsd>
                        <HoldingRoi $positive={roiPct === null ? null : roiPct >= 0}>
                          {roiPct !== null ? `${roiPct >= 0 ? '+' : ''}${roiPct.toFixed(1)}%` : '—'}
                        </HoldingRoi>
                      </HoldingRight>
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <HelperText>No token holdings yet.</HelperText>
            )}
          </>
        )}
      </Card>
      {!loading && !error && telegramUser && wallet && (
        <Card>
          <SnsRegisterRow>
            <SnsRegisterInfo>
              <SnsRegisterIcon src="/sns-icon.png" alt="SNS" />
              <SnsRegisterLabel>Register a .sol domain name</SnsRegisterLabel>
            </SnsRegisterInfo>
            <SnsRegisterButton type="button" onClick={openSnsModal}>
              Register
            </SnsRegisterButton>
          </SnsRegisterRow>
        </Card>
      )}
      {!loading && !error && telegramUser && wallet && (
        <Card>
          <HarvestHeader>
            <SectionLabel>Harvest Yield</SectionLabel>
            <HarvestToggle type="button" onClick={() => setHarvestExpanded(!harvestExpanded)}>
              {harvestExpanded ? 'Hide' : 'Show More'}
            </HarvestToggle>
          </HarvestHeader>
          {harvestExpanded && (
            <>
              <HelperText>
                Harvest wallet: {' '}
                {wallet?.harvestWalletAddress ? (
                  <WalletAddressButton type="button" onClick={handleCopyHarvestWallet} title="Copy harvest wallet address">
                    {formatWalletAddress(wallet.harvestWalletAddress)}
                  </WalletAddressButton>
                ) : (
                  '—'
                )}
              </HelperText>
              <HelperText>
                WSOL balance: {wsolBalance !== null ? `${wsolBalance.toFixed(6)} WSOL` : '—'}
              </HelperText>
              <HarvestButton type="button" onClick={handleUnwrapWsol} disabled={unwrapping || !wsolBalance}>
                {unwrapping ? 'Unwrapping…' : 'Unwrap SOL'}
              </HarvestButton>
              {unwrapStatus && <HelperText>{unwrapStatus}</HelperText>}
              <Label>Withdraw from harvest wallet</Label>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={harvestWithdrawToMain}
                  onChange={(event) => setHarvestWithdrawToMain(event.target.checked)}
                />
                Send to main wallet
              </CheckboxRow>
              {!harvestWithdrawToMain && (
                <Input
                  type="text"
                  placeholder="Destination SOL address"
                  value={harvestWithdrawAddress}
                  onChange={(event) => setHarvestWithdrawAddress(event.target.value)}
                />
              )}
              {!harvestWithdrawAll && (
                <Input
                  type="number"
                  placeholder="Amount in SOL"
                  value={harvestWithdrawAmount}
                  onChange={(event) => setHarvestWithdrawAmount(event.target.value)}
                  min="0"
                  step="0.000001"
                />
              )}
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={harvestWithdrawAll}
                  onChange={(event) => setHarvestWithdrawAll(event.target.checked)}
                />
                Withdraw all (keeps a small reserve for network fees)
              </CheckboxRow>
              <HarvestButton
                type="button"
                onClick={handleHarvestWithdraw}
                disabled={!canHarvestWithdraw || harvestWithdrawLoading}
              >
                {harvestWithdrawLoading ? 'Withdrawing…' : 'Withdraw SOL'}
              </HarvestButton>
              {harvestWithdrawStatus && <HelperText>{harvestWithdrawStatus}</HelperText>}
              {harvestLoading && <HelperText>Loading LP positions…</HelperText>}
              {!harvestLoading && harvestError && <HelperText>{harvestError}</HelperText>}
              {!harvestLoading && !harvestError && harvestPositions.length === 0 && (
                <HelperText>No LP positions found.</HelperText>
              )}
              {!harvestLoading && !harvestError && harvestPositions.length > 0 && (
                <List>
                  {harvestPositions.map((position) => (
                    <ListItem key={position.positionMint}>
                      <HoldingLeft>
                        <HoldingLogo
                          src={position.logoUrl || '/token-placeholder.png'}
                          alt={`${position.label} logo`}
                          onError={(event) => {
                            (event.target as HTMLImageElement).src = '/token-placeholder.png';
                          }}
                        />
                        <HoldingSymbol>{position.label}</HoldingSymbol>
                      </HoldingLeft>
                      <HarvestRight>
                        <HarvestBadge $locked={position.isLocked}>
                          {position.isLocked ? 'Locked' : 'Available'}
                        </HarvestBadge>
                        <HarvestButton
                          type="button"
                          onClick={() => handleHarvestPosition(position)}
                          disabled={harvestingPosition === position.positionMint}
                        >
                          {harvestingPosition === position.positionMint ? 'Harvesting…' : 'Harvest'}
                        </HarvestButton>
                      </HarvestRight>
                    </ListItem>
                  ))}
                </List>
              )}
              {harvestStatus && (
                <HelperText>
                  {harvestStatus}{' '}
                  {harvestLink && (
                    <HarvestLink href={harvestLink} target="_blank" rel="noreferrer">
                      View on Solscan
                    </HarvestLink>
                  )}
                </HelperText>
              )}
            </>
          )}
        </Card>
      )}
      {!loading && !error && telegramUser && wallet && (
        <Card>
          <BundleHeader>
            <SectionLabel>Bundle Wallets</SectionLabel>
            <BundleToggle type="button" onClick={() => setBundleExpanded(!bundleExpanded)}>
              {bundleExpanded ? 'Hide' : 'Show More'}
            </BundleToggle>
          </BundleHeader>
          {bundleExpanded && (
            <>
              <HelperText>
                Display your bundle wallets and manage scale-out rules.
              </HelperText>
              <HelperText>Scanning all bundle wallets tied to your account.</HelperText>
              <FormGroup>
                <Label>Token filter</Label>
                <Select
                  value={bundleTokenFilterMint}
                  onChange={(event) => setBundleTokenFilterMint(event.target.value)}
                  disabled={bundleWalletsLoading || bundleTokenOptions.length === 0}
                >
                  <option value="all">All tokens</option>
                  {bundleTokenOptions.map((option) => (
                    <option key={option.mint} value={option.mint}>
                      {option.symbol ? `${option.symbol} • ` : ''}{option.mint.slice(0, 6)}...{option.mint.slice(-4)}
                    </option>
                  ))}
                </Select>
              </FormGroup>
              <Label>Withdraw destination</Label>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={bundleWithdrawToMain}
                  onChange={(event) => setBundleWithdrawToMain(event.target.checked)}
                />
                Send to main wallet
              </CheckboxRow>
              {!bundleWithdrawToMain && (
                <Input
                  type="text"
                  placeholder="Destination SOL address"
                  value={bundleWithdrawAddress}
                  onChange={(event) => setBundleWithdrawAddress(event.target.value)}
                />
              )}
              <BundleButtonRow>
                <BundleActionButton
                  type="button"
                  $danger
                  onClick={() => setBundleResetOpen(true)}
                  disabled={bundleWalletsLoading || bundleWallets.length === 0}
                >
                  Reset wallets
                </BundleActionButton>
                {bundleWallets.length > 0 && (
                  <BundleActionButton type="button" $secondary onClick={copyBundleWallets}>
                    Copy list
                  </BundleActionButton>
                )}
                {bundleWalletDisplay.length > 0 && (
                  <BundleActionButton
                    type="button"
                    $danger
                    onClick={() => setBundleSellAllOpen(true)}
                    disabled={bundleWalletsSellingAll}
                  >
                    {bundleWalletsSellingAll ? 'Selling all…' : 'Sell all'}
                  </BundleActionButton>
                )}
                {bundleWalletDisplay.length > 0 && (
                  <BundleActionButton
                    type="button"
                    $secondary
                    onClick={() => setBundleWithdrawAllOpen(true)}
                    disabled={bundleWalletsWithdrawingAll}
                  >
                    {bundleWalletsWithdrawingAll ? 'Withdrawing all…' : 'Withdraw all SOL'}
                  </BundleActionButton>
                )}
              </BundleButtonRow>
              <BundleWarning>
                Resetting will permanently replace these wallets. Any funds left in them will be lost.
              </BundleWarning>
              <BundleHeader>
                <SectionLabel>Bundle Scale Out</SectionLabel>
                <BundleToggle type="button" onClick={() => setScaleOutExpanded(!scaleOutExpanded)}>
                  {scaleOutExpanded ? 'Hide' : 'Show More'}
                </BundleToggle>
              </BundleHeader>
              {scaleOutExpanded && (
                <>
                  {bundleTokenOptions.length > 0 ? (
                    <BundleWalletCard>
                      <HelperText>
                        When market cap stays above the target, sells up to 30% of 5m buy volume.
                        Total sells are capped at your % of market cap.
                      </HelperText>
                      <FormGroup>
                        <Label>Token</Label>
                        <Select
                          value={scaleOutMint}
                          onChange={(event) => {
                            const nextMint = event.target.value;
                            setScaleOutMint(nextMint);
                            setBundleTokenFilterMint(nextMint);
                          }}
                          disabled={scaleOutLoading}
                        >
                          {bundleTokenOptions.map((option) => (
                            <option key={option.mint} value={option.mint}>
                              {option.symbol ? `${option.symbol} • ` : ''}{option.mint.slice(0, 6)}...{option.mint.slice(-4)}
                            </option>
                          ))}
                        </Select>
                      </FormGroup>
                      <HelperText>
                        Total tokens across bundle wallets: {' '}
                        {scaleOutTokenTotals.totalTokens.toLocaleString('en-US', { maximumFractionDigits: 6 })}{' '}
                        {scaleOutTokenTotals.symbol || ''}
                      </HelperText>
                      <FormGroup>
                        <Label>Token sell amount</Label>
                        <ScaleOutOptionRow>
                          {(['10', '25', '50', '75', 'max'] as const).map((option) => (
                            <ScaleOutOptionButton
                              key={option}
                              type="button"
                              $active={scaleOutTokenPercent === option}
                              onClick={() => setScaleOutTokenPercent(option)}
                            >
                              {option === 'max' ? 'Max' : `${option}%`}
                            </ScaleOutOptionButton>
                          ))}
                        </ScaleOutOptionRow>
                        <HelperText>
                          Selected tokens: {scaleOutTokenTargetAmount.toLocaleString('en-US', { maximumFractionDigits: 6 })}{' '}
                          {scaleOutTokenTotals.symbol || ''}
                        </HelperText>
                      </FormGroup>
                      <FormGroup>
                        <Label>Target Market Cap (USD)</Label>
                        <Input
                          type="text"
                          placeholder="1,000,000"
                          value={formatNumberWithCommas(scaleOutTargetMarketCap)}
                          onChange={(event) => {
                            const raw = event.target.value.replace(/[^\d]/g, '');
                            setScaleOutTargetMarketCap(raw);
                          }}
                          disabled={scaleOutLoading}
                        />
                      </FormGroup>
                      <FormGroup>
                        <Label>Scale Out Percentage</Label>
                        <Select
                          value={scaleOutPercent}
                          onChange={(event) => setScaleOutPercent(event.target.value as '10' | '20' | '30')}
                          disabled={scaleOutLoading}
                        >
                          <option value="10">10%</option>
                          <option value="20">20%</option>
                          <option value="30">30%</option>
                        </Select>
                      </FormGroup>
                      <HelperText>
                        Estimated return at target market cap: {scaleOutTargetMarketCapValue > 0
                          ? `$${scaleOutEstimatedUsd.toFixed(2)}`
                          : '—'}
                      </HelperText>
                      <ToggleRow>
                        <input
                          type="checkbox"
                          checked={scaleOutEnabled}
                          onChange={(event) => setScaleOutEnabled(event.target.checked)}
                          disabled={scaleOutLoading}
                        />
                        Enable scale out
                      </ToggleRow>
                      <BundleButtonRow>
                        <BundleActionButton
                          type="button"
                          onClick={handleSaveScaleOut}
                          disabled={scaleOutLoading}
                        >
                          {scaleOutLoading ? 'Saving…' : 'Save scale out'}
                        </BundleActionButton>
                      </BundleButtonRow>
                      {scaleOutError && <HelperText>{scaleOutError}</HelperText>}
                      {scaleOutStatus && <HelperText>{scaleOutStatus}</HelperText>}
                    </BundleWalletCard>
                  ) : (
                    <HelperText>
                      Bundle scale out needs a bundle wallet holding a token first.
                    </HelperText>
                  )}
                </>
              )}
              {bundleWalletDisplay.length > 0 && (
                <BundleTotalsRow>
                  <div>
                    Total SOL: <BundleTotalsValue>{bundleTotals.solTotal.toFixed(4)}</BundleTotalsValue>
                  </div>
                  <div>
                    Token USD: <BundleTotalsValue>${bundleTotals.tokenUsdTotal.toFixed(2)}</BundleTotalsValue>
                  </div>
                  <div>
                    Tokens Held: <BundleTotalsValue>
                      {bundleTokenFilterMint === 'all'
                        ? '—'
                        : `${bundleTokenTotals.totalTokens.toLocaleString('en-US', { maximumFractionDigits: 6 })} ${bundleTokenTotals.symbol}`.trim()}
                    </BundleTotalsValue>
                  </div>
                  <div>
                    Total USD: <BundleTotalsValue>${bundleTotals.totalUsd.toFixed(2)}</BundleTotalsValue>
                  </div>
                </BundleTotalsRow>
              )}
              {bundleWalletsError && <HelperText>{bundleWalletsError}</HelperText>}
              {bundleWalletsStatus && <HelperText>{bundleWalletsStatus}</HelperText>}
              {bundleWalletDisplay.length > 0 && (
                <BundleWalletList>
                  {bundleWalletDisplay.map((detail) => {
                    const solUsd = detail.solBalance * effectiveSolPrice;
                    const activeHolding = detail.filteredHolding ?? detail.primaryHolding ?? null;
                    const tokenAmount = activeHolding?.amount ?? null;
                    const tokenSymbol = activeHolding?.symbol || '';
                    const tokenUsd = bundleTokenFilterMint === 'all' ? detail.totalTokenUsd : detail.filteredTokenUsd;
                    const sellResult = bundleWalletSellResults[detail.walletAddress];
                    const withdrawResult = bundleWalletWithdrawResults[detail.walletAddress];
                    const solReceivedUsd = sellResult ? sellResult.solReceived * effectiveSolPrice : 0;
                    return (
                      <BundleWalletCard key={detail.walletAddress}>
                        <BundleWalletHeader>
                          <BundleWalletIndex>Wallet {detail.walletIndex + 1}</BundleWalletIndex>
                          <BundleWalletAddressRow>
                            {truncateAddress(detail.walletAddress)}
                            <BundleWalletActionButton
                              type="button"
                              onClick={() => copyBundleWallet(detail.walletAddress)}
                            >
                              Copy
                            </BundleWalletActionButton>
                          </BundleWalletAddressRow>
                        </BundleWalletHeader>
                        <BundleWalletMetaRow>
                          <div>
                            SOL: <BundleWalletValue>{detail.solBalance.toFixed(4)}</BundleWalletValue>
                          </div>
                          <div>
                            USD: <BundleWalletValue>${solUsd.toFixed(2)}</BundleWalletValue>
                          </div>
                        </BundleWalletMetaRow>
                        <BundleWalletMetaRow>
                          <div>
                            Token: <BundleWalletValue>
                              {tokenAmount !== null
                                ? `${tokenAmount.toLocaleString('en-US', { maximumFractionDigits: 6 })} ${tokenSymbol}`.trim()
                                : '—'}
                            </BundleWalletValue>
                          </div>
                          <div>
                            Token value: <BundleWalletValue>
                              {activeHolding ? `$${tokenUsd.toFixed(2)} ${tokenSymbol}`.trim() : '—'}
                            </BundleWalletValue>
                          </div>
                        </BundleWalletMetaRow>
                        <BundleWalletActionRow>
                          <BundleWalletActionButton
                            type="button"
                            $danger
                            disabled={!activeHolding || bundleWalletsSelling[detail.walletAddress]}
                            onClick={() => {
                              if (!activeHolding) return;
                              handleSellBundleWallet(detail.walletAddress, activeHolding.mint);
                            }}
                          >
                            {bundleWalletsSelling[detail.walletAddress] ? 'Selling…' : 'Sell'}
                          </BundleWalletActionButton>
                          <BundleWalletActionButton
                            type="button"
                            disabled={bundleWalletsWithdrawing[detail.walletAddress]}
                            onClick={() => handleWithdrawBundleWallet(detail.walletAddress)}
                          >
                            {bundleWalletsWithdrawing[detail.walletAddress] ? 'Withdrawing…' : 'Withdraw'}
                          </BundleWalletActionButton>
                        </BundleWalletActionRow>
                        {sellResult && (
                          <HelperText>
                            Sold {sellResult.tokensSold.toLocaleString('en-US', { maximumFractionDigits: 6 })}{' '}
                            {sellResult.symbol || 'tokens'} for{' '}
                            {sellResult.solReceived.toFixed(4)} SOL
                            {effectiveSolPrice ? ` ($${solReceivedUsd.toFixed(2)})` : ''}.{' '}
                            <HarvestLink href={`https://solscan.io/tx/${sellResult.signature}`} target="_blank" rel="noreferrer">
                              View on Solscan
                            </HarvestLink>
                          </HelperText>
                        )}
                        {withdrawResult && (
                          <HelperText>
                            Withdrew {withdrawResult.amountSol.toFixed(4)} SOL.{' '}
                            <HarvestLink href={`https://solscan.io/tx/${withdrawResult.signature}`} target="_blank" rel="noreferrer">
                              View on Solscan
                            </HarvestLink>
                          </HelperText>
                        )}
                      </BundleWalletCard>
                    );
                  })}
                </BundleWalletList>
              )}
              {bundleWalletDisplay.length === 0 && bundleWallets.length > 0 && (
                <HelperText>Bundle wallets loaded. No balances yet.</HelperText>
              )}
            </>
          )}
        </Card>
      )}
      {!loading && !error && telegramUser && wallet && (
        <Card>
          <HarvestHeader>
            <SectionLabel>Recent Swaps</SectionLabel>
            <HarvestToggle type="button" onClick={() => setRecentSwapsExpanded(!recentSwapsExpanded)}>
              {recentSwapsExpanded ? 'Hide' : 'Show More'}
            </HarvestToggle>
          </HarvestHeader>
          {recentSwapsExpanded && (
            <>
              {loadingSwaps ? (
                <HelperText>Loading recent swaps…</HelperText>
              ) : recentSwaps.length === 0 ? (
                <HelperText>No recent swaps available.</HelperText>
              ) : (
                <List>
                  {recentSwaps.map((swap) => {
                    const amountText = formatSwapTokenAmount(swap);
                    const symbolText = swap.tokenSymbol || holdingsByMint.get(swap.mintAddress)?.symbol || 'TOKEN';
                    const usdValue = swap.solAmount !== null && swap.solAmount !== undefined
                      ? `$${((swap.solAmount || 0) * effectiveSolPrice).toFixed(2)}`
                      : '—';
                    return (
                      <ListItem key={swap.id}>
                        <div>
                          <SwapLabel $direction={swap.direction}>
                            {swap.direction.toUpperCase()}
                            {amountText ? ` • ${amountText}` : ''}
                            {` ${symbolText}`}
                          </SwapLabel>
                        </div>
                        <div>
                          {swap.direction === 'sell' ? usdValue : usdValue}
                        </div>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </>
          )}
          {!recentSwapsExpanded && (
            <HelperText>Show more to view your recent swaps.</HelperText>
          )}
        </Card>
      )}
      {bundleResetOpen && (
        <ModalBackdrop onClick={() => setBundleResetOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Reset bundle wallets?</ModalTitle>
              <CloseButton type="button" onClick={() => setBundleResetOpen(false)}>×</CloseButton>
            </ModalHeader>
            <BundleWarning>
              This will permanently replace your bundle wallets. Any funds left in them will be lost.
            </BundleWarning>
            <ModalActionButton
              type="button"
              onClick={async () => {
                setBundleResetOpen(false);
                await loadBundleWallets({ reset: true });
              }}
            >
              Yes, reset wallets
            </ModalActionButton>
          </ModalCard>
        </ModalBackdrop>
      )}
      {bundleSellAllOpen && (
        <ModalBackdrop onClick={() => setBundleSellAllOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Sell all bundle wallets?</ModalTitle>
              <CloseButton type="button" onClick={() => setBundleSellAllOpen(false)}>×</CloseButton>
            </ModalHeader>
            <HelperText>
              This will sell all token holdings across your bundle wallets. Make sure you want to close these positions.
            </HelperText>
            <ModalActionButton
              type="button"
              onClick={async () => {
                setBundleSellAllOpen(false);
                await handleSellAllBundleWallets();
              }}
              disabled={bundleWalletsSellingAll}
            >
              {bundleWalletsSellingAll ? 'Selling all…' : 'Yes, sell all'}
            </ModalActionButton>
          </ModalCard>
        </ModalBackdrop>
      )}
      {bundleWithdrawAllOpen && (
        <ModalBackdrop onClick={() => setBundleWithdrawAllOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Withdraw all bundle wallet SOL?</ModalTitle>
              <CloseButton type="button" onClick={() => setBundleWithdrawAllOpen(false)}>×</CloseButton>
            </ModalHeader>
            <HelperText>
              This will move SOL from every bundle wallet {bundleWithdrawToMain ? 'back to your main wallet' : 'to the destination address'}.
            </HelperText>
            <ModalActionButton
              type="button"
              onClick={async () => {
                setBundleWithdrawAllOpen(false);
                await handleWithdrawAllBundleWallets();
              }}
              disabled={bundleWalletsWithdrawingAll}
            >
              {bundleWalletsWithdrawingAll ? 'Withdrawing all…' : 'Yes, withdraw all'}
            </ModalActionButton>
          </ModalCard>
        </ModalBackdrop>
      )}
      {snsModalOpen && (
        <ModalBackdrop onClick={() => setSnsModalOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Register a .sol domain</ModalTitle>
              <CloseButton type="button" onClick={() => setSnsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            <HelperText>
              Search for a .sol domain and register it directly. We will swap SOL to USDC from your
              Sentry wallet to cover the registration fee.
            </HelperText>
            <Input
              type="text"
              placeholder="Domain name (no .sol)"
              value={snsDomainInput}
              onChange={(event) => {
                setSnsDomainInput(event.target.value);
                setSnsCheckResult(null);
                setSnsCheckStatus(null);
                setSnsRegisterStatus(null);
              }}
            />
            <ModalActionButton
              type="button"
              onClick={handleCheckSnsDomain}
              disabled={!snsDomainInput.trim() || snsChecking}
            >
              {snsChecking ? 'Checking…' : 'Search availability'}
            </ModalActionButton>
            {snsCheckResult && (
              <AvailabilityRow $available={snsCheckResult.available}>
                <AvailabilityIcon>{snsCheckResult.available ? '✓' : '✕'}</AvailabilityIcon>
                <div>
                  {snsCheckResult.domain} is {snsCheckResult.available ? 'available' : 'not available'}!
                </div>
              </AvailabilityRow>
            )}
            {snsCheckResult?.available && (
              <>
                <HelperText>
                  Registration price: ${snsCheckResult.priceUsd.toFixed(2)}.
                  {' '}Exact fee: {snsCheckResult.priceUsdc.toFixed(2)} USDC.
                </HelperText>
                <ModalActionButton
                  type="button"
                  onClick={handleRegisterSnsDomain}
                  disabled={snsRegistering}
                >
                  {snsRegistering ? 'Registering…' : `Register ${snsCheckResult.domain}`}
                </ModalActionButton>
              </>
            )}
            {snsCheckStatus && <HelperText>{snsCheckStatus}</HelperText>}
            {snsRegisterStatus && <HelperText>{snsRegisterStatus}</HelperText>}
          </ModalCard>
        </ModalBackdrop>
      )}
      {withdrawOpen && (
        <ModalBackdrop onClick={() => setWithdrawOpen(false)}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Withdraw SOL</ModalTitle>
              <CloseButton type="button" onClick={() => setWithdrawOpen(false)}>×</CloseButton>
            </ModalHeader>
            <Input
              type="text"
              placeholder="Destination SOL address"
              value={withdrawAddress}
              onChange={(event) => setWithdrawAddress(event.target.value)}
            />
            {!withdrawAll && (
              <Input
                type="number"
                placeholder="Amount in SOL"
                value={withdrawAmount}
                onChange={(event) => setWithdrawAmount(event.target.value)}
                min="0"
                step="0.000001"
              />
            )}
            <CheckboxRow>
              <input
                type="checkbox"
                checked={withdrawAll}
                onChange={(event) => setWithdrawAll(event.target.checked)}
              />
              Withdraw all (keeps a small reserve for network fees)
            </CheckboxRow>
            <HelperText>SOL withdrawals only. Sentry tokens stay on-platform.</HelperText>
            <ModalActionButton type="button" onClick={handleWithdraw} disabled={!canWithdraw}>
              Withdraw SOL
            </ModalActionButton>
            {withdrawStatus && <HelperText>{withdrawStatus}</HelperText>}
          </ModalCard>
        </ModalBackdrop>
      )}
    </PageContainer>
  );
};

export default SentryWalletPWA;
